import type { PricingCampaignsScope } from '@/entities/pricing-campaign'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	AlertInfoModel,
	OptimizationGroupModel,
	PricingCampaignOverviewModel,
} from '@/shared/generated'

import React, {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
} from 'react'
import {
	any,
	filter,
	groupBy,
	keyBy,
	map,
	negate,
	orderBy,
	pipe,
	prop,
	sum,
	values,
} from 'lodash/fp'

import { useSelectedCurrency } from '@/entities/currency'
import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { usePricingAlertsQuery } from '@/entities/price-alert'
import {
	getAssortmentShare,
	getCountsByPc,
	getOgsByPc,
	isGlobalPricingCampaign,
	usePricingCampaignsOverviewQuery,
	useProductsCountsByPcQuery,
} from '@/entities/pricing-campaign'

import { Operators } from '@/shared/components/query-builder'
import { AlertGroupBy } from '@/shared/generated'
import { filterComparator } from '@/shared/tools/filtering'

export type PricingCampaignTableRow = {
	campaign: PricingCampaignOverviewModel
	optimization_group_name?: string | null
	alerts_count: number
	assortment_share: number | null
	products_count: number
	optimization_groups?: string[]
	order: number
}
export type PricingCampaignContextProps = {
	setSelected(selected: string[]): void
	selected: string[]
	isLoading: boolean
	scope: PricingCampaignsScope
	data: PricingCampaignTableRow[]
	filteredRows: PricingCampaignTableRow[]
	staticFilter: 'all' | 'local' | 'global'
	setStaticFilter(staticFilter: 'all' | 'local' | 'global'): void
	rules: FilterSchema[]
	setRules(rules: FilterSchema[]): void
}

export const PricingCampaignsContext =
	createContext<PricingCampaignContextProps>({} as any)

export function PricingCampaignsProvider({
	children,
	scope,
}: {
	children: React.ReactNode
	scope: PricingCampaignsScope
}) {
	const optimizationGroupId = scope?.optimization_group_id
	const currency = useSelectedCurrency()

	/**
	 * Hooks below should be used only for Optimization Group Scope
	 * if it is not Optimization Group Scope, then hooks below will just pass
	 */

	const [selected, setSelected] = useState<string[]>([])
	const [staticFilter, setStaticFilter] = useState<'all' | 'local' | 'global'>(
		'all'
	)
	const [rules, setRules] = useState<FilterSchema[]>([])
	/**
	 * Queries
	 */

	const campaigns = usePricingCampaignsOverviewQuery<
		PricingCampaignOverviewModel[]
	>(scope, {
		select: orderBy<PricingCampaignOverviewModel>(
			['optimization_group_id', 'name'],
			['desc', 'asc']
		),
	})

	const alerts = usePricingAlertsQuery<Record<string, AlertInfoModel[]>>(
		{
			group_by: AlertGroupBy.PricingCampaignId,
			optimization_group_ids: optimizationGroupId ? [optimizationGroupId] : [],
			currency,
		},
		{
			select: pipe([prop('by_level'), groupBy(prop('pricing_campaign_id'))]),
		}
	)
	// Filters for products counts by pc query and for total products count (optimization group scope)
	const ogScopeFilters = useMemo(
		() => ({
			filters: scope?.optimization_group_id
				? [
						{
							name: 'optimization_group_id',
							operation: Operators.IS as const,
							value: scope.optimization_group_id,
						},
					]
				: [],
		}),
		[scope]
	)

	const optimizationGroupsQuery = useOptimizationGroupsQuery<
		Record<string, OptimizationGroupModel>
	>({
		select: keyBy<OptimizationGroupModel>('id'),
	})
	const productsCountsByPc = useProductsCountsByPcQuery(ogScopeFilters)
	/**
	 * Mutations
	 */

	/**
	 * Computed
	 */
	const isLoading = any(prop('isLoading'), [
		campaigns,
		productsCountsByPc,
		optimizationGroupsQuery,
	])

	const pcIdsWithProducts = useMemo(
		() => productsCountsByPc.data?.map(prop('pricing_campaign_id')) || [],
		[productsCountsByPc.data]
	)

	const getDataList = useCallback(
		(data?: PricingCampaignOverviewModel[]): PricingCampaignTableRow[] => {
			const countsDict = getCountsByPc(productsCountsByPc.data)
			const totalProductsCount = sum(values(countsDict))
			const groupsDict = getOgsByPc(productsCountsByPc.data)
			const getOGName = (campaign: PricingCampaignOverviewModel) =>
				campaign.optimization_group_id
					? optimizationGroupsQuery.data?.[campaign.optimization_group_id]?.name
					: null
			const extendWithCustomData = (
				campaign: PricingCampaignOverviewModel
			): PricingCampaignTableRow => ({
				order: campaign.order,
				campaign,
				alerts_count: getSumOfAlerts(alerts.data?.[campaign.id] || []),
				assortment_share: getAssortmentShare(
					totalProductsCount,
					countsDict?.[campaign.id] || 0
				),
				products_count: countsDict?.[campaign.id] || 0,
				optimization_groups: groupsDict?.[campaign.id] || [],
				optimization_group_name: getOGName(campaign),
			})

			return pipe([filter(prop('id')), map(extendWithCustomData)])(data)
		},
		[alerts.data, productsCountsByPc.data, optimizationGroupsQuery.data]
	)
	const data: PricingCampaignTableRow[] = useMemo(() => {
		const list = getDataList(campaigns.data || [])

		if (!optimizationGroupId) {
			return sortByOrder(list)
		} else {
			const pcsRelatedToOg = list.filter(
				({ campaign }) =>
					!isGlobalPricingCampaign(campaign) ||
					pcIdsWithProducts.includes(campaign.id)
			)

			return sortByOrder([
				...getSortedGlobalPcs(pcsRelatedToOg).map((item, index) => ({
					...item,
					order: index + 1,
				})),
				...pcsRelatedToOg.filter(
					pipe([prop('campaign'), negate(isGlobalPricingCampaign)])
				),
			])
		}
	}, [campaigns.data, optimizationGroupId, pcIdsWithProducts, getDataList])
	const filterByRules = useCallback(
		(data: PricingCampaignTableRow[]) =>
			data.filter((item) =>
				rules.every((filter: FilterSchema) => filterComparator(filter, item))
			),
		[rules]
	)
	// Filter data by rules and search text and static filter
	const filteredRows = useMemo(() => {
		const rows = filterByRules(data) as PricingCampaignTableRow[]
		if (staticFilter === 'local') {
			return rows.filter((row) => !isGlobalPricingCampaign(row.campaign))
		} else if (staticFilter === 'global') {
			return rows.filter((row) => isGlobalPricingCampaign(row.campaign))
		} else {
			return rows
		}
	}, [data, staticFilter, filterByRules])

	const value = useMemo(
		() => ({
			isLoading,
			data,
			selected,
			setSelected,
			scope,
			filteredRows,
			staticFilter,
			setStaticFilter,
			rules,
			setRules,
		}),
		[
			isLoading,
			data,
			selected,
			setSelected,
			scope,
			filteredRows,
			staticFilter,
			setStaticFilter,
			rules,
			setRules,
		]
	)

	return (
		<PricingCampaignsContext value={value}>{children}</PricingCampaignsContext>
	)
}

export const usePricingCampaigns = () => {
	const context = use(PricingCampaignsContext)
	if (!context) {
		throw new Error(
			'usePricingCampaigns must be used within a PricingCampaignsProvider'
		)
	}

	return context
}
/**
 * Helpers functions
 */
const getSumOfAlerts = pipe([map(prop('products')), sum])
const sortByOrder = orderBy<PricingCampaignTableRow>(['order'], ['asc'])
const getSortedGlobalPcs: (
	data: PricingCampaignTableRow[]
) => PricingCampaignTableRow[] = pipe([
	filter(pipe([prop('campaign'), isGlobalPricingCampaign])),
	sortByOrder,
])
